/**
 * 행 컬렉션 등록형(registered) 도메인 위젯 — 개선 포인트(사례) AI 이미지(improvement-image).
 * (결정 2026-09-14-risk-assessment-report-composed-transition §4 — 구 RA 작성 페이지의
 *  searchOrGenerateImage/regenerateImage 이식. 선언은 템플릿, 실행은 호스트.)
 *
 * 값 형태: { url: string; prompt?: string; model?: string; imageId?: string; usageCount?: number }
 * (계약 REGISTERED_OBJECT_FIELD_CONTRACTS['improvement-image']와 1:1 — 본문 image 위젯은 `<필드>.url`).
 *
 * 흐름(편집 + 호스트 배선 시):
 *   (진입 자동) 마운트 시 url 비어 있음 + 개선대책 있음 → generating → 커밋. 인스턴스당 1회, 행 N개
 *              병렬(구 for 루프 동형). 실패 → error + 버튼으로 재시도.
 *   [새로 생성] / [다시 생성] → generating → 커밋 url·prompt·model
 *   [지우기] → 빈 값 { url: '' }
 *   상태 문구·실패 문구는 IMPROVEMENT_IMAGE_TEXT(계약 텍스트). 실패 시 값 불변.
 *   유사 사례 검색(「유사 사례 찾기」·후보 목록)은 2026-09-14 사용자 결정으로 제거 — 이미지 임베딩 도입 시
 *   재추가(improvementImageHost.ts 헤더). 구 문서가 실은 imageId·usageCount는 표시(출처 요약)만 한다.
 * 입력(finding)은 행 객체(context.scopeValues — 브리지·행 그리드가 싣는다)에서 고정 키로 만든다.
 *
 * degrade: 호스트 미배선(캔버스·external 무인증) 또는 editable=false면 **표시 전용** — 이미지가
 * 있으면 미리보기(image 위젯 동형 READONLY_IMAGE_STYLE), 없으면 안내 문구. 절대 [object Object] 미표시.
 * 캔버스(admin 라이브)는 호스트가 없어 자연히 표시 전용이라 별도 inert 축이 필요 없다.
 *
 * rc Tailwind 규약: 앱에 흔한 유틸만 클래스로, 레이아웃·색은 인라인 소유. 버튼은 addItemButtonStyle 승계.
 */
import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { READONLY_IMAGE_STYLE } from '../AnnotatedImageView';
import { ImageWithFallback } from '../ImageWithFallback';
import { addItemButtonStyle, addItemButtonDisabledStyle } from '../addItemButton';
import type { RegisteredRowWidget } from '../RowsCollectionEditor';
import {
  EMPTY_IMPROVEMENT_IMAGE,
  IMPROVEMENT_IMAGE_TEXT,
  asImprovementImageValue,
  extractImprovementImageHost,
  extractRowScope,
  findingFromRow,
  runImprovementImageAutoOnce,
  runImprovementImageGenerate,
  type ImprovementImageStatus,
  type ImprovementImageValue,
} from './improvementImageHost';

// RowsCollectionEditor readonly 셀 톤(표시 전용 안내 칸).
const readonlyValueClass =
  'w-full px-2 py-1.5 border border-gray-200 bg-gray-50 rounded text-sm text-gray-500 whitespace-pre-wrap break-words';
const readonlyValueStyle: CSSProperties = { minHeight: '2rem' };

// 버튼 줄 — 가로 flex + 줄바꿈(임계 레이아웃 인라인 소유).
const actionRowStyle: CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 };
const smallButtonStyle: CSSProperties = { ...addItemButtonStyle, padding: '4px 10px', fontSize: 12 };
const smallButtonDisabledStyle: CSSProperties = {
  ...smallButtonStyle,
  ...addItemButtonDisabledStyle,
};
const metaTextStyle: CSSProperties = { marginTop: 4, fontSize: 11, color: '#6b7280' };
const errorTextStyle: CSSProperties = { marginTop: 4, fontSize: 12, color: '#dc2626' };

// 생성 중 표시 — 문장 대신 **이미지 칸 자리에 로딩 인디케이터**(2026-09-14 사용자 요청). 이미지가 아직
// 없으면 이미지와 같은 발자국(READONLY_IMAGE_STYLE 높이)의 빈 칸에, 다시 생성 중이면 기존 이미지 위에
// 반투명 오버레이로 얹는다 — 행 높이가 생성 전후로 흔들리지 않는다. 스피너는 앱에 이미 흔한
// `animate-spin`(rc ReportWriteShell·StickyHeader 선례)만 쓰고 나머지는 인라인(rc Tailwind 신규 유틸 금지).
const generatingBoxStyle: CSSProperties = {
  ...READONLY_IMAGE_STYLE,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px dashed #c7d2fe',
  borderRadius: 4,
};
const generatingWrapStyle: CSSProperties = { position: 'relative' };
const generatingOverlayStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(255,255,255,0.6)',
};
const spinnerStyle: CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: '50%',
  border: '3px solid #e0e7ff',
  borderTopColor: '#4338ca',
};

/** 생성 중 인디케이터 — 스피너 + 접근성 이름(문구는 IMPROVEMENT_IMAGE_TEXT.generating 그대로). */
const GeneratingIndicator = () => (
  <div role="status" aria-live="polite" aria-label={IMPROVEMENT_IMAGE_TEXT.generating} title={IMPROVEMENT_IMAGE_TEXT.generating}>
    <div className="animate-spin" style={spinnerStyle} />
  </div>
);

/**
 * 값의 출처 요약 — 재사용(사례 ID·횟수 — 변환된 구 문서만 싣는다) / 생성(모델). 둘 다 없으면 빈 문자열.
 */
export function summarizeImprovementImage(v: ImprovementImageValue): string {
  if (v.imageId) {
    return `재사용 사례${typeof v.usageCount === 'number' ? ` · ${v.usageCount}회 사용` : ''}`;
  }
  if (v.model || v.prompt) return `AI 생성${v.model ? ` · ${v.model}` : ''}`;
  return '';
}

/** 표시 전용(호스트 미배선·읽기 전용) — 이미지 미리보기 또는 안내. */
function ReadonlyDisplay({ v }: { v: ImprovementImageValue }) {
  if (!v.url) {
    return (
      <div className={readonlyValueClass} style={readonlyValueStyle}>
        {IMPROVEMENT_IMAGE_TEXT.empty}
      </div>
    );
  }
  const meta = summarizeImprovementImage(v);
  return (
    <div>
      <ImageWithFallback src={v.url} alt="개선 포인트 이미지" style={READONLY_IMAGE_STYLE} />
      {meta ? <p style={metaTextStyle}>{meta}</p> : null}
    </div>
  );
}

const ImprovementImageWidget: RegisteredRowWidget = ({ value, onChange, editable, context }) => {
  const current = asImprovementImageValue(value);
  const host = extractImprovementImageHost(context);
  const [status, setStatus] = useState<ImprovementImageStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  // await 사이 언마운트(행 삭제·화면 이탈) 감지 — stale 커밋/상태쓰기 차단(runPhotoUpload isAlive 동형).
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);
  // 커밋은 **최신 렌더의 onChange**로 — 반복 행의 onChange(makeRowScopedOnChange)는 렌더 시점 rows에서
  // 출발하므로, 흐름 시작 시점 클로저로 커밋하면 그 사이 다른 행이 커밋한 값을 옛 rows로 덮어쓴다
  // (진입 시 자동 실행은 행 N개가 병렬로 돌아 이 경로가 확정적으로 발생 — 마지막 행만 살아남음).
  // 최신 onChange는 최신 rows(+같은 이벤트 pending 누적)에서 출발해 앞선 커밋을 보존한다.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const finding = findingFromRow(extractRowScope(context));
  const sinks = {
    host,
    finding,
    busy: status === 'generating',
    isAlive: () => aliveRef.current,
    setStatus,
    setError,
    commit: (next: ImprovementImageValue) => {
      onChangeRef.current(next);
    },
  };
  // 진입 시 자동 생성 — 구 RA 페이지 동형(항목 로드 직후 개선대책 있는 행 전부). 인스턴스당 1회(autoRanRef)
  // — 재렌더·값 변경·행 재정렬에 재발동하지 않는다. 조건·흐름은 runImprovementImageAutoOnce(순수 정본)가
  // 소유하고 여기서는 마운트 시점에 한 번 묻는다.
  const autoRanRef = useRef(false);
  useEffect(() => {
    runImprovementImageAutoOnce({
      guard: autoRanRef,
      editable,
      value: current,
      host,
      finding,
      isAlive: () => aliveRef.current,
      setStatus,
      setError,
      commit: sinks.commit,
    });
    // 마운트 시 1회(deps 없음, 의도) — 이후 값·호스트 변화는 자동 실행 대상이 아니다(사용자가 개선대책을
    // 타이핑하는 도중 발동하면 안 된다). 행은 rowId 키라 prefill 행은 값이 실린 채로 마운트된다.
  }, []);

  if (!editable || !host) return <ReadonlyDisplay v={current} />;

  const busy = sinks.busy;
  const btn = (label: string, onClick: () => void, key: string) => (
    <button
      key={key}
      type="button"
      style={busy ? smallButtonDisabledStyle : smallButtonStyle}
      disabled={busy}
      onClick={() => {
        if (busy) return;
        onClick();
      }}
    >
      {label}
    </button>
  );
  const generate = () => void runImprovementImageGenerate(sinks);
  const clear = () => {
    setError(null);
    setStatus('idle');
    onChange(EMPTY_IMPROVEMENT_IMAGE);
  };

  const meta = summarizeImprovementImage(current);
  return (
    <div>
      {current.url ? (
        <div>
          <div style={generatingWrapStyle}>
            <ImageWithFallback src={current.url} alt="개선 포인트 이미지" style={READONLY_IMAGE_STYLE} />
            {status === 'generating' ? (
              <div style={generatingOverlayStyle}>
                <GeneratingIndicator />
              </div>
            ) : null}
          </div>
          {meta ? <p style={metaTextStyle}>{meta}</p> : null}
        </div>
      ) : status === 'generating' ? (
        <div style={generatingBoxStyle}>
          <GeneratingIndicator />
        </div>
      ) : null}
      <div style={actionRowStyle}>
        {btn(current.url ? '다시 생성' : '새로 생성', generate, 'generate')}
        {current.url ? btn('지우기', clear, 'clear') : null}
      </div>
      {error ? <p style={errorTextStyle}>{error}</p> : null}
    </div>
  );
};

export default ImprovementImageWidget;
